"use client";

import { Format, getFormatDisplayName } from '../../lib/formats';
import { CodeBlockLine, SupernovaCodeBlock } from '@/components/ui';
import { cn } from '../../lib/utils';

interface PandasConversionProps {
  from: Format;
  to: Format;
}

function PandasConversion({ from, to }: PandasConversionProps) {
  return (
    <>
      <p>First, we need to install pandas</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install pandas"}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`Then we can load the ${from} file into a dataframe`}</p>
      <PandasFromStatement from={from} />
      <p>{`Finally, we can export the dataframe to the ${to} format`}</p>
      <PandasToStatement to={to} />
    </>
  );
}

interface DuckDBPythonConversionProps {
  from: Format;
  to: Format;
}

function DuckDBPythonConversion({ from, to }: DuckDBPythonConversionProps) {
  if (from === Format.PARQUET) {
    return <DuckDBPythonFromParquetConversion to={to} />;
  }
  return (
    <>
      <p>First, we need to install duckdb for Python</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install duckdb"}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`The following DuckDB query will read a ${from} file and output a ${to} file`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`duckdb.sql("""COPY (select * from 'path/to/file.${from.toLowerCase()}') TO ${copyToString({ to })}""")`}</CodeBlockLine>
      </SupernovaCodeBlock>
    </>
  );
}

interface DuckDBPythonFromParquetConversionProps {
  to: Format;
}

function DuckDBPythonFromParquetConversion({ to }: DuckDBPythonFromParquetConversionProps) {
  return (
    <>
      <p>First, we need to install duckdb for Python</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install duckdb"}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`The following duckdb query will copy the contents of a single Parquet file to a ${to} file`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`duckdb.sql("""COPY (select * from 'path/to/file.parquet') TO ${copyToString({ to })}""")`}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>
        If you have more than one Parquet file with the same schema (e.g. your
        Parquet files are partitioned) then you can use the following
      </p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`duckdb.sql("""COPY (select * from read_parquet(['path/to/file1.parquet', 'path/to/file2.parquet'])) TO ${copyToString({ to })}""")`}</CodeBlockLine>
      </SupernovaCodeBlock>
    </>
  );
}

interface CopyToStringProps {
  to: Format;
}

function copyToString({ to }: CopyToStringProps) {
  if (to === Format.CSV) {
    return `'path/to/file.${to.toLowerCase()}' (HEADER, FORMAT '${to.toLowerCase()}')`;
  } else {
    return `'path/to/file.${to.toLowerCase()}' (FORMAT '${to.toLowerCase()}')`;
  }
}

interface PandasFromStatementProps {
  from: Format;
}

function PandasFromStatement({ from }: PandasFromStatementProps) {
  if (from === Format.CSV) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>{"df = pd.read_csv('path/to/file.csv')"}</CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (from === Format.TSV) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df = pd.read_csv('path/to/file.tsv', sep='\\t')"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (from === Format.PARQUET) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df = pd.read_parquet('path/to/file.parquet')"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (from === Format.XLSX) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df = pd.read_excel('path/to/file.xlsx')"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (from === Format.JSON) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df = pd.read_json('path/to/file.json')"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else {
    console.error({ from });
    throw new Error("Missing From");
  }
}

interface PandasToStatementProps {
  to: Format;
}

function PandasToStatement({ to }: PandasToStatementProps) {
  if (to === Format.CSV) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df.to_csv('/path/to/file.csv', index=False)"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (to === Format.TSV) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df.to_csv('path/to/file.tsv', sep='\\t', index=False)"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (to === Format.PARQUET) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df.to_parquet('path/to/file.parquet', index=False)"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (to === Format.XLSX) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df.to_excel('path/to/file.xlsx', index=False)"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else if (to === Format.JSON) {
    return (
      <SupernovaCodeBlock>
        <CodeBlockLine>
          {"df.to_json('path/to/file.json', index=False)"}
        </CodeBlockLine>
      </SupernovaCodeBlock>
    );
  } else {
    console.error({ to });
    throw new Error("Missing extension");
  }
}

interface ClickHouseConversionProps {
  from: Format;
  to: Format;
}

function ClickHouseConversion({ from, to }: ClickHouseConversionProps) {
  return (
    <>
      <p>First, we need to install the ClickHouse client for Python</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install clickhouse-connect"}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>{`Then we can use ClickHouse to convert from ${getFormatDisplayName(from)} to ${getFormatDisplayName(to)}`}</p>
      
      <p>Start by importing the necessary libraries and connecting to ClickHouse:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"import clickhouse_connect"}</CodeBlockLine>
        <CodeBlockLine>{"import os"}</CodeBlockLine>
        <CodeBlockLine>{""}</CodeBlockLine>
        <CodeBlockLine>{"# Connect to ClickHouse"}</CodeBlockLine>
        <CodeBlockLine>{"client = clickhouse_connect.get_client(host='localhost', port=8123)"}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Define your input and output file paths:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`# Define input and output file paths`}</CodeBlockLine>
        <CodeBlockLine>{`input_file = 'path/to/input.${from.toLowerCase()}'`}</CodeBlockLine>
        <CodeBlockLine>{`output_file = 'path/to/output.${to.toLowerCase()}'`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      {from === Format.CSV && to === Format.PARQUET ? (
        <>
          <p>Now perform the direct conversion from CSV to Parquet:</p>
          <SupernovaCodeBlock>
            <CodeBlockLine>{"# Direct conversion from CSV to Parquet"}</CodeBlockLine>
            <CodeBlockLine>{"client.command(f'''"}</CodeBlockLine>
            <CodeBlockLine>{"INSERT INTO FUNCTION file('{output_file}', Parquet)"}</CodeBlockLine>
            <CodeBlockLine>{"SELECT *"}</CodeBlockLine>
            <CodeBlockLine>{"FROM file('{input_file}', 'CSV', 'column1 String, column2 String, column3 String')"}</CodeBlockLine>
            <CodeBlockLine>{"''')"}</CodeBlockLine>
          </SupernovaCodeBlock>
          
          <p>Confirm the conversion was successful:</p>
          <SupernovaCodeBlock>
            <CodeBlockLine>{"print(f'Successfully converted {input_file} to {output_file}')"}</CodeBlockLine>
          </SupernovaCodeBlock>
        </>
      ) : (
        <>
          <p>{`Now perform the direct conversion from ${getFormatDisplayName(from)} to ${getFormatDisplayName(to)}:`}</p>
          <SupernovaCodeBlock>
            <CodeBlockLine>{`# Direct conversion from ${from} to ${to}`}</CodeBlockLine>
            <CodeBlockLine>{"client.command(f'''"}</CodeBlockLine>
            <CodeBlockLine>{`INSERT INTO FUNCTION file('{output_file}', '${to}')`}</CodeBlockLine>
            <CodeBlockLine>{"SELECT *"}</CodeBlockLine>
            <CodeBlockLine>{`FROM file('{input_file}', '${from}')`}</CodeBlockLine>
            <CodeBlockLine>{"''')"}</CodeBlockLine>
          </SupernovaCodeBlock>
          
          <p>Confirm the conversion was successful:</p>
          <SupernovaCodeBlock>
            <CodeBlockLine>{"print(f'Successfully converted {input_file} to {output_file}')"}</CodeBlockLine>
          </SupernovaCodeBlock>
        </>
      )}
      
      <p>For more complex conversions or when you need to transform the data during conversion, you can use a table:</p>
      
      <p>First, create a table from the input file:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"# Create a table from the input file"}</CodeBlockLine>
        <CodeBlockLine>{"client.command(f'''"}</CodeBlockLine>
        <CodeBlockLine>{"CREATE TABLE IF NOT EXISTS conversion_data AS"}</CodeBlockLine>
        <CodeBlockLine>{`SELECT * FROM file('{input_file}', '${from}')`}</CodeBlockLine>
        <CodeBlockLine>{"''')"}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Then, perform any transformations and export to the destination format:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"# Now you can perform transformations if needed"}</CodeBlockLine>
        <CodeBlockLine>{"client.command(f'''"}</CodeBlockLine>
        <CodeBlockLine>{`INSERT INTO FUNCTION file('{output_file}', '${to}')`}</CodeBlockLine>
        <CodeBlockLine>{"SELECT * FROM conversion_data"}</CodeBlockLine>
        <CodeBlockLine>{"# Add WHERE, GROUP BY, or other clauses as needed"}</CodeBlockLine>
        <CodeBlockLine>{"''')"}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Finally, clean up by dropping the temporary table:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"# Clean up"}</CodeBlockLine>
        <CodeBlockLine>{"client.command('DROP TABLE IF EXISTS conversion_data')"}</CodeBlockLine>
        <CodeBlockLine>{"print(f'Successfully converted {input_file} to {output_file}')"}</CodeBlockLine>
      </SupernovaCodeBlock>
    </>
  );
}

interface HowToConvertProps {
  from: Format;
  to: Format;
  className?: string;
}

export function HowToConvert({ from, to, className }: HowToConvertProps) {
  // Check if the formats are supported by Pandas, DuckDB, and ClickHouse
  const pandasSupport = true; // All formats in Format enum are supported by Pandas
  const duckSupport = true; // All formats in Format enum are supported by DuckDB
  const clickHouseSupport = true; // All formats in Format enum are supported by ClickHouse

  if (!pandasSupport && !duckSupport && !clickHouseSupport) {
    return null;
  }

  let availableMethodsClause = "";
  if (pandasSupport && duckSupport && clickHouseSupport) {
    availableMethodsClause = "Pandas, DuckDB, or ClickHouse";
  } else if (pandasSupport && duckSupport) {
    availableMethodsClause = "Pandas or DuckDB";
  } else if (pandasSupport && clickHouseSupport) {
    availableMethodsClause = "Pandas or ClickHouse";
  } else if (duckSupport && clickHouseSupport) {
    availableMethodsClause = "DuckDB or ClickHouse";
  } else if (pandasSupport) {
    availableMethodsClause = "Pandas";
  } else if (duckSupport) {
    availableMethodsClause = "DuckDB";
  } else if (clickHouseSupport) {
    availableMethodsClause = "ClickHouse";
  }

  return (
    <div className={`my-8 ${className}`}>
      <div className="mb-4">
        <div className="text-2xl font-bold text-foreground">
          <h2>{`How to convert ${getFormatDisplayName(from)} to ${getFormatDisplayName(to)} in Python`}</h2>
        </div>
      </div>
      <div>
        <div className="prose max-w-none dark:prose-invert">
          <p>
            We can convert {getFormatDisplayName(from)} to {getFormatDisplayName(to)} in Python
            using {availableMethodsClause}
          </p>
          {pandasSupport && (
            <>
              <h3 className="text-foreground">{`How to Convert ${getFormatDisplayName(from)} to ${getFormatDisplayName(to)} using Pandas`}</h3>
              <PandasConversion from={from} to={to} />
            </>
          )}
          {duckSupport && (
            <>
              <h3 className="text-foreground">{`How to Convert ${getFormatDisplayName(from)} to ${getFormatDisplayName(to)} using DuckDB`}</h3>
              <DuckDBPythonConversion from={from} to={to} />
            </>
          )}
          {clickHouseSupport && (
            <>
              <h3 className="text-foreground">{`How to Convert ${getFormatDisplayName(from)} to ${getFormatDisplayName(to)} using ClickHouse`}</h3>
              <ClickHouseConversion from={from} to={to} />
            </>
          )}
        </div>
      </div>
    </div>
  );
} 